import React, { useState } from 'react';
import { dehydrate, QueryClient } from '@tanstack/react-query';
import { Skeleton } from 'antd';
import classNames from 'classnames';
import { GetStaticProps } from 'next';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';
import { StringParam, useQueryParams, withDefault } from 'use-query-params';

import {
  formatSubject,
  getSubjects,
  globalConst,
  isEmptyArr,
  onTrackYandexGoal,
  parseSubjectName,
  useIsMobile
} from 'modules/global';
import { getTopicTests, TopicTestLayout, topicTestsConst, useTopicTests } from 'modules/mavzuli-test';

import {
  CustomPagination,
  EmptyData,
  LimitFinishedModal,
  LinkWrapper,
  NextHead,
  SearchInput,
  TestCard,
  Visible
} from 'components';

const Index = () => {
  const t = useTranslations();
  const { locale = 'uz', query } = useRouter();
  const [queryParams] = useQueryParams({
    search: withDefault(StringParam, ''),
    page: withDefault(StringParam, '1')
  });

  const [isLoaded, setIsLoaded] = useState(false);
  const isMobile = useIsMobile();
  const selectedSubject = query?.subject !== t('barchasi') ? query?.subject : undefined;
  const banner = topicTestsConst?.subjectBannerMap[query?.subject as string];

  const { data, isLoading, refetch } = useTopicTests({
    search: queryParams?.search || undefined,
    page: queryParams?.page as string,
    languageId: globalConst.langIds[locale],
    name: selectedSubject as string
  });

  return (
    <>
      <NextHead
        title={t('meta.topic_test_subject.title', {
          subject: (query?.subject as string).toUpperCase()
        })}
        description={t('meta.topic_test_subject.description', {
          subject: (query?.subject as string).toUpperCase()
        })}
      />
      <TopicTestLayout>
        <div className="shadow-xs mb-4 hidden items-center justify-between rounded-xl bg-white px-4 py-3 lg:flex">
          <div className="flex items-center gap-2 text-lg font-medium">{formatSubject(query?.subject as string)}</div>
          <SearchInput loading={isLoading} refetch={refetch} />
        </div>

        <Visible
          if={isLoading}
          else={
            <Visible
              if={isEmptyArr(data?.data)}
              else={
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3">
                  {data?.data?.map((savedTest, index) => (
                    <React.Fragment key={savedTest?.test_id}>
                      <TestCard
                        key={savedTest?.test_id}
                        isNew={savedTest?.is_new}
                        testId={savedTest?.test_id}
                        teacher={savedTest?.teacher}
                        isSaved={savedTest?.is_saved}
                        testUrl={savedTest?.test_url}
                        viewCount={savedTest?.view_count}
                        testCount={savedTest?.test_count}
                        testRating={savedTest?.test_rating}
                        lessonName={savedTest?.lesson_name}
                        subjectName={savedTest?.subject_name}
                        learningCenter={savedTest?.learning_center}
                      />

                      {banner && ((index === 1 && isMobile) || (index === 5 && !isMobile)) ? (
                        <LinkWrapper
                          target="_blank"
                          isNotLink={banner?.isNotLink}
                          onClick={() => onTrackYandexGoal(globalConst.yandexGoalKeys.rising_zone)}
                          className="h-26 relative col-span-1 w-full rounded-xl sm:col-span-2 sm:h-40 md:col-span-3 lg:col-span-3"
                          href={banner?.targetUrl ? banner.targetUrl : 'https://akam.uz'}
                        >
                          <div className="h-26 relative col-span-1 w-full rounded-xl sm:col-span-2 sm:h-40 md:col-span-3 lg:col-span-3">
                            {!banner?.videoURL && !isLoaded && (
                              <div className="absolute inset-0 z-0 h-full w-full animate-pulse rounded-xl bg-gray-200" />
                            )}
                            {banner?.videoURL ? (
                              <video
                                src={banner?.videoURL}
                                className="h-full rounded-xl object-cover"
                                autoPlay
                                muted
                                loop
                              />
                            ) : (
                              <Image
                                src={banner?.insideCardBanner}
                                alt="schools banner"
                                title="Maktablarning banneri (reklama)"
                                fill
                                onLoadingComplete={() => setIsLoaded(true)}
                                className={classNames('rounded-xl', {
                                  visible: isLoaded,
                                  invisible: !isLoaded
                                })}
                              />
                            )}
                          </div>
                        </LinkWrapper>
                      ) : null}
                    </React.Fragment>
                  ))}
                </div>
              }
            >
              <EmptyData title={t('common.empty_test_data')} />
            </Visible>
          }
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 10 }).map((_, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Skeleton.Button key={index} active className="!h-40 !w-full !rounded-xl" />
            ))}
          </div>
        </Visible>
        {data?.data && (
          <div className="mt-5">
            <CustomPagination total={data?.total} />
          </div>
        )}
      </TopicTestLayout>

      <LimitFinishedModal />
    </>
  );
};

export default Index;

export const getStaticProps: GetStaticProps = async ({ locale, params }) => {
  const queryClient = new QueryClient();
  const languageId = globalConst.langIds[locale || 'uz'];
  const subject = parseSubjectName(params?.subject as string);

  await Promise.allSettled([
    queryClient.prefetchQuery({
      queryKey: [topicTestsConst.queryKeys.USE_TOPIC_TEST, '1', languageId, subject, null],
      queryFn: getTopicTests({ page: '1', languageId, name: subject })
    }),
    queryClient.prefetchQuery({
      queryKey: [globalConst.queryKeys.SUBJECTS_DATA],
      queryFn: getSubjects
    })
  ]);

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      messages: (await import(`../../../messages/${locale}.json`)).default
    },
    revalidate: 604800
  };
};

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking'
  };
}
